'use client';

import { ReactNode } from 'react';
import { getDescriptionForLiqRecord } from 'src/utils/liquidation';
import { abbreviateAddress } from 'src/utils/strings';
import { useDrawersStore } from 'src/stores/useDrawersStore';
import SideDrawer from 'src/components/Drawers/BaseDrawers/SideDrawer/SideDrawer';
import IconButton from 'src/components/primitives/IconButton/IconButton';
import { Swap } from '@drift-labs/icons';
import useDateDisplayState from 'src/hooks/useDateDisplayState';
import TableV2 from 'src/components/Tables/TableV2';
import Link from 'src/components/primitives/Link/Link';
import LiquidationDetailsDrawerTable from 'src/components/Drawers/HistoryDrawers/LiquidationDetailsDrawer/LiquidationDetailsDrawerTable/LiquidationDetailsDrawerTable';
import Divider from 'src/components/primitives/Divider/Divider';

export default function LiquidationDetailsDrawer() {
	const { displayDatesAsTimestamp, toggleDateDisplayType } =
		useDateDisplayState();

	const liquidation = useDrawersStore((s) => s.liquidations.liquidation);

	if (!liquidation) {
		return null;
	}

	return (
		<SideDrawer.Container drawerName="liquidations">
			<SideDrawer.Title>Liquidation Details</SideDrawer.Title>
			<SideDrawer.Body>
				<div className="flex flex-col gap-y-sm">
					<SummaryRow
						label={
							<>
								Time
								<IconButton
									size="sm"
									icon={Swap}
									className="-m-md p-md"
									variant="tertiary"
									onClick={toggleDateDisplayType}
								/>
							</>
						}
						value={
							<TableV2.DateCell
								className="w-fit py-0 whitespace-nowrap"
								date={new Date(liquidation.ts * 1000)}
								displayAsTimestamp={displayDatesAsTimestamp}
							/>
						}
					/>
					<SummaryRow
						label="Liquidatee"
						value={
							<Link
								variant="primary"
								href={`https://app.drift.trade/?userAccount=${liquidation.liquidations[0]?.user.toString()}`}
								openInNewTab
							>
								{abbreviateAddress(liquidation.liquidations[0]?.user)}
							</Link>
						}
					/>
					<SummaryRow
						label="Notional Debt Transferred"
						value={liquidation.notionalAmount.toNotional()}
					/>
					<SummaryRow
						label="Liquidation Summary"
						value={getDescriptionForLiqRecord(liquidation)}
					/>
				</div>

				<Divider />

				<LiquidationDetailsDrawerTable liquidation={liquidation} />
			</SideDrawer.Body>
		</SideDrawer.Container>
	);
}

interface SummaryProps {
	label: ReactNode;
	value: ReactNode;
}

function SummaryRow({ label, value }: SummaryProps) {
	return (
		<div className="flex w-full justify-between typo-b4">
			<span className="flex items-center gap-x-xs text-text-secondary">
				{label}
			</span>
			<span className="text-text-default">{value}</span>
		</div>
	);
}
